import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, output, signal, ViewChild } from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import { SdDialog, SdRadioGroup } from '@solid-design-system/components';
import { Dialog } from 'src/app/common/util/dialog';
import { LoadingIndicatorDirective } from 'src/app/common/directives/loading-indicator.directive';
import { ExportFormat, ExportScope } from './export.model';

/** Emitted when the user confirms the dialog. The caller is responsible for triggering the actual export. */
export interface ExportDialogResult {
  format: ExportFormat;
  scope: ExportScope;
}

interface ScopeOption {
  scope: ExportScope;
  label: string;
  description: string;
}

/**
 * Export dialog shown when the user triggers an export while filters are active ("Szenario 2").
 * It lets the user choose between exporting the currently filtered view or the whole fund.
 *
 * This component only collects the user's choice - it never calls a backend itself. On confirm,
 * it emits {@link exported} and switches into a loading state; the caller (DashboardComponent)
 * performs the actual export call and must invoke either {@link hide} on success or
 * {@link showError} on failure. This mirrors the pattern used by
 * `SaveFilterTemplateDialogComponent` / `FilterSidepanelComponent`, so wiring up a real backend
 * call later only requires changes in `DashboardComponent`, not in this dialog.
 */
@Component({
  selector: 'app-export-dialog',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [TranslocoModule, LoadingIndicatorDirective],
  templateUrl: './export-dialog.component.html',
  styleUrl: './export-dialog.component.scss',
})
export class ExportDialogComponent extends Dialog {
  readonly headline = 'dashboard.exportDialog.title';
  readonly cancelLabel = 'dashboard.exportDialog.cancel';
  readonly confirmLabel = 'dashboard.exportDialog.confirm';
  readonly questionLabel = 'dashboard.exportDialog.question';
  readonly questionWithFundLabel = 'dashboard.exportDialog.questionWithFund';

  /** Selectable scope options, rendered in a loop instead of duplicating markup per option. */
  protected readonly scopeOptions: ScopeOption[] = [
    {
      scope: 'current',
      label: 'dashboard.exportDialog.currentView',
      description: 'dashboard.exportDialog.currentViewDescription',
    },
    { scope: 'all', label: 'dashboard.exportDialog.allData', description: 'dashboard.exportDialog.allDataDescription' },
  ];

  public readonly exported = output<ExportDialogResult>();

  protected readonly format = signal<ExportFormat>('CSV');
  protected readonly activeFilterLabels = signal<string[]>([]);
  protected readonly fund = signal<string | undefined>(undefined);
  protected readonly scope = signal<ExportScope>('current');
  protected readonly saving = signal<boolean>(false);
  protected readonly errorMessage = signal<string | null>(null);

  @ViewChild('dialog')
  private readonly dialog!: ElementRef<SdDialog>;

  get canConfirm(): boolean {
    return !this.saving();
  }

  open(format: ExportFormat, activeFilterLabels: string[] = [], fund?: string): void {
    this.format.set(format);
    this.activeFilterLabels.set(activeFilterLabels);
    this.fund.set(fund);
    this.scope.set('current');
    this.saving.set(false);
    this.errorMessage.set(null);
    this.dialog?.nativeElement?.show();
  }

  hide(): void {
    this.saving.set(false);
    this.dialog?.nativeElement?.hide();
  }

  showError(message: string): boolean {
    this.saving.set(false);
    if (!this.isOpen()) {
      return false;
    }
    this.errorMessage.set(message);
    return true;
  }

  isOpen(): boolean {
    return this.dialog?.nativeElement?.open ?? false;
  }

  selectScope(event: Event): void {
    const radioGroup = (event.currentTarget ?? event.target) as SdRadioGroup & { value?: ExportScope };
    const value = radioGroup.value;
    if (value === 'current' || value === 'all') {
      this.scope.set(value);
    }
  }
  confirm(): void {
    if (!this.canConfirm) {
      return;
    }
    this.errorMessage.set(null);
    this.saving.set(true);
    this.exported.emit({ format: this.format(), scope: this.scope() });
  }

  cancel(): void {
    this.dialog?.nativeElement?.hide();
  }

  protected onAfterHide(): void {
    this.saving.set(false);
    this.errorMessage.set(null);
  }
}

